import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_real-estate-hub-147/artifacts/e5h2jhc8_LOGO_Graphique.png";
const LOGO_TEXT = "https://customer-assets.emergentagent.com/job_real-estate-hub-147/artifacts/tdjh637n_LOGO_Titre.png";

const navLinks = [
  { href: "/", label: "Accueil" },
  { href: "/biens", label: "Nos biens" },
  { href: "/a-propos", label: "L'agence" },
  { href: "/actualites", label: "Actualités" },
  { href: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";
  const isAbout = location.pathname === "/a-propos" || location.pathname === "/agence";
  const showTransparent = (isHome || isAbout) && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showTransparent
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-md border-b border-black/5 shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src={LOGO_ICON}
              alt="Vingt-Neuf"
              className="h-10 w-10 object-contain rounded"
            />
            <img
              src={LOGO_TEXT}
              alt="Vingt-Neuf Immobilier"
              className={`h-8 object-contain transition-all duration-300 ${
                showTransparent ? "brightness-200" : ""
              }`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]/g, '')}`}
                className={`nav-link text-[11px] uppercase tracking-[0.2em] font-medium transition-colors duration-300 ${
                  showTransparent
                    ? "text-white/90 hover:text-white"
                    : "text-[#777777] hover:text-[#699ab6]"
                } ${location.pathname === link.href ? (showTransparent ? "text-white" : "text-[#699ab6]") : ""}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA + Phone + Email */}
          <div className="hidden lg:flex items-center gap-5">
            <div className="flex flex-col items-end gap-0.5">
              <a
                href="tel:+41223000262"
                data-testid="nav-phone"
                className={`flex items-center gap-1.5 text-xs font-light transition-colors duration-300 ${
                  showTransparent ? "text-white/80" : "text-[#777777]"
                }`}
              >
                <Phone className="w-3 h-3" />
                +41 22 300 02 62
              </a>
              <a
                href="mailto:info@vingt-neuf.ch"
                data-testid="nav-email"
                className={`flex items-center gap-1.5 text-xs font-light transition-colors duration-300 ${
                  showTransparent ? "text-white/70" : "text-[#777777]"
                }`}
              >
                <Mail className="w-3 h-3" />
                info@vingt-neuf.ch
              </a>
            </div>
            <Link
              to="/contact"
              data-testid="nav-contact-btn"
              className={`text-[11px] uppercase tracking-[0.15em] font-medium px-6 py-2.5 transition-all duration-300 ${
                showTransparent
                  ? "border border-white/40 text-white hover:bg-white hover:text-[#333333]"
                  : "bg-[#699ab6] text-white hover:bg-[#5a8ba5]"
              }`}
            >
              Nous contacter
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 transition-colors ${
              showTransparent ? "text-white" : "text-[#333333]"
            }`}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-black/5">
          <div className="px-6 py-8 space-y-6">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-mobile-${link.label.toLowerCase().replace(/[^a-z]/g, '')}`}
                className="block text-[11px] uppercase tracking-[0.2em] font-medium text-[#777777] hover:text-[#699ab6]"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-black/5 space-y-2">
              <a
                href="tel:+41223000262"
                className="flex items-center gap-2 text-sm text-[#777777]"
              >
                <Phone className="w-3.5 h-3.5" />
                +41 22 300 02 62
              </a>
              <a
                href="mailto:info@vingt-neuf.ch"
                className="flex items-center gap-2 text-sm text-[#777777]"
              >
                <Mail className="w-3.5 h-3.5" />
                info@vingt-neuf.ch
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
